import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listAccounts,
  getAccount,
  updateAccount,
  researchAccount,
  getAccountActivities,
} from "@/api/accounts";
import { createEngagement } from "@/api/engagements";
import type { EngagementEventInput } from "@sales-ops-amplify/shared";

const ACCOUNTS_QUERY_KEY = ["accounts"] as const;

export function useAccounts(params?: {
  search?: string;
  industry?: string;
  country?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: [...ACCOUNTS_QUERY_KEY, "list", params ?? {}],
    queryFn: () => listAccounts(params),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useAccount(accountId: string | undefined) {
  return useQuery({
    queryKey: [...ACCOUNTS_QUERY_KEY, "detail", accountId],
    queryFn: () => getAccount(accountId!),
    enabled: !!accountId,
    staleTime: 30_000,
  });
}

export function useUpdateAccount(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (updates: Record<string, unknown>) =>
      updateAccount(accountId, updates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ACCOUNTS_QUERY_KEY });
    },
  });
}

export function useResearchAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) => researchAccount(accountId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ACCOUNTS_QUERY_KEY });
    },
  });
}

export function useAccountActivities(
  accountId: string | undefined,
  types?: string[],
) {
  return useQuery({
    queryKey: [...ACCOUNTS_QUERY_KEY, "activities", accountId, types ?? []],
    queryFn: () => getAccountActivities(accountId!, { types }),
    enabled: !!accountId,
    staleTime: 15_000,
  });
}

export function useLogAccountActivity(accountId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EngagementEventInput) => createEngagement(input),
    onSuccess: () => {
      if (accountId) {
        void qc.invalidateQueries({
          queryKey: [...ACCOUNTS_QUERY_KEY, "activities", accountId],
        });
      }
    },
  });
}

/** @deprecated Prefer useAccountActivities */
export const useCompanyActivities = useAccountActivities;
/** @deprecated Prefer useLogAccountActivity */
export const useLogCompanyActivity = useLogAccountActivity;
